import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeTableModule } from 'primeng/treetable';

import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../../app.settings';

import { notifyAndTrackError } from '../../../../common/error';
import { isEmpty, isStringEqual } from '../../../../common/functions';
import { successMsg } from '../../../../common/messages';

import { AuditInfoComponent, CanComponentDeactivate } from '../../../../components';

import { AppInsightsService } from '../../../../services/app-insights.service';
import {
  CatalogAdminService,
  DictionaryCacheService,
  ICatalogSubstrate,
  ICatalogSubstrateAddRequest,
  ICatalogSubstrateUpdateRequest,
  IDictionaryEntry,
  ISubstrate
} from '../../../../services/insecta';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'tab-substrate',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ContextMenuModule,
    CommonModule,
    DatePickerModule,
    DialogModule,
    FloatLabelModule,
    FieldsetModule,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    RouterModule,
    SelectModule,
    TableModule,
    TabsModule,
    TextareaModule,
    ToggleSwitchModule,
    TreeSelectModule,
    TreeTableModule,
    AuditInfoComponent,
    TooltipModule
  ],

  templateUrl: 'tab-substrates.component.html'
})
export class TabSubstrateComponent implements OnDestroy, OnInit, CanComponentDeactivate, OnChanges {
  dirtyConfirm$: ((leave: boolean) => void) | null = null;
  emptyGuid: string = `00000000-0000-0000-0000-000000000000`;

  pageVars: any = {
    isDirty: false,
    isEditing: false,
    isReady: false,
    pageId: `Admin:SiteCatalog`,
    showDirtyDlg: false
  };
  dlgVars: any = {
    substrates: <IDictionaryEntry[]>[],
    visible: false
  };
  isSubstratesDialogValid: boolean = true;

  @Input() isRecipesLookupForbidden: boolean = false;
  @Input() currentUser: string = ``;
  @Input() catalogId: string = this.emptyGuid;
  @Input() catalogSubstrates: ICatalogSubstrate[] = [];
  @Input() recipesLookup: any[] = [];
  @Output('isReady') isReady = new EventEmitter<boolean>();

  substrates: IDictionaryEntry[] = [];

  substrateId?: string;
  leadTime?: number;
  isAvailable: boolean = true;
  isExternal: boolean = false;

  constructor(
    private appInsights: AppInsightsService,
    private catalogService: CatalogAdminService,
    private dictionaryCache: DictionaryCacheService,
    private msgService: MessageService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:SiteCatalog:SubstratesTab`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/sitecatalog#tabsubstrates`);
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) return Promise.resolve(true);

    return new Promise((resolve) => {
      this.pageVars.showDirtyDlg = true;
      const confirm$ = (decision: boolean) => {
        this.dirtyConfirm$ = null;
        resolve(decision);
      };
      this.dirtyConfirm$ = confirm$;
    });
  }

  dirtyConfirm(leave: boolean) {
    this.pageVars.showDirtyDlg = false;
    if (this.dirtyConfirm$) {
      this.dirtyConfirm$(leave);
    }
  }

  ngOnDestroy() {
    this.pageVars = {
      isReady: false
    };
    this.catalogSubstrates = [];
  }

  ngOnInit() {
    this.isReady.emit(false);
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);
    this.pageVars.isReady = false;

    const request = {
      substrates: this.dictionaryCache.get('substrate')
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.substrates = response.substrates;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabSubstrateComponent', action: 'Init:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
        this.isReady.emit(true);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['catalogSubstrates'] && !isEmpty(changes['catalogSubstrates'].currentValue)) {
      const mappedSubstrates = this.catalogSubstrates.map((s: ICatalogSubstrate) => {
        const obj = s.recipeJson != null && s.recipeJson !== '{}' && s.recipeJson !== '' ? JSON.parse(s.recipeJson) : {};
        const recipeId = obj.recipeId;
        return { ...s, recipeId: recipeId };
      });
      this.catalogSubstrates = mappedSubstrates;
    }
  }

  updateCatalogSubstrate(catalogSubstrate: ICatalogSubstrate) {
    const request = <ICatalogSubstrateUpdateRequest>{
      id: catalogSubstrate.id,
      leadTime: catalogSubstrate.leadTime,
      isAvailable: catalogSubstrate.isAvailable,
      isExternal: catalogSubstrate.isExternal,
      isActive: catalogSubstrate.isActive,
      recipeJson: catalogSubstrate.recipeJson,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalogSubstrate(catalogSubstrate.catalogId, request))
      .then(() => {
        const idx = this.catalogSubstrates.findIndex((x) => x.id === catalogSubstrate.id);
        this.catalogSubstrates[idx].action = 'UPD';
        this.catalogSubstrates[idx].actionBy = this.currentUser;
        this.catalogSubstrates[idx].actionOn = new Date();

        successMsg(this.msgService, 'Catalog Substrate has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabSubstrateComponent', action: 'UpdateCatalogSubstrate:Error' });
      });
  }

  dlgCancel() {
    this.dlgVars.visible = false;
    this.dlgVars.substrates = [];
    this.dlgVars.substrate = null;
    this.computeSubstratesDialogValidation();
  }

  dlgNewDisabled() {
    const result = this.substrates.filter((s) => isEmpty(this.catalogSubstrates.find((cs) => cs.substrateId === s.id))).length === 0;
    return this.pageVars.isEditing || result;
  }

  dlgNewRow() {
    this.dlgVars.substrates = this.substrates.filter((s) => this.catalogSubstrates.find((cs) => isStringEqual(cs.substrateId, s.id)) === undefined);
    this.dlgVars.substrate = <ICatalogSubstrate>{
      id: this.emptyGuid,
      catalogId: this.catalogId,
      substrateId: this.emptyGuid,
      leadTime: 1,
      isAvailable: true,
      isExternal: true,
      isActive: true
    };
    this.dlgVars.visible = true;
    this.substrateId = this.dlgVars.substrate.substrateId;
    this.leadTime = this.dlgVars.substrate.leadTime;
    this.computeSubstratesDialogValidation();
  }

  dlgSave() {
    this.isReady.emit(false);

    const request = <ICatalogSubstrateAddRequest>{
      catalogId: this.catalogId,
      substrateId: this.substrateId,
      leadTime: this.leadTime,
      isAvailable: this.isAvailable,
      isExternal: this.isExternal,
      isActive: true,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.addCatalogSubstrate(request))
      .then((result) => {
        const catalogSubstrate = <ICatalogSubstrate>{
          id: result,
          catalogId: request.catalogId,
          substrateId: request.substrateId,
          leadTime: request.leadTime,
          isAvailable: request.isAvailable,
          isExternal: request.isExternal,
          isActive: request.isActive,
          action: 'INS',
          actionBy: request.actionBy,
          actionOn: new Date(),
          substrate: <ISubstrate>{
            id: this.substrateId,
            name: this.substrates.find((x) => isStringEqual(x.id, <string>this.substrateId))?.text
          }
        };
        this.catalogSubstrates.push(catalogSubstrate);
        this.catalogSubstrates = this.catalogSubstrates.sort((x: ICatalogSubstrate, y: ICatalogSubstrate) =>
          x.substrate.name > y.substrate.name ? 1 : -1
        );

        successMsg(this.msgService, 'Catalog Substrate has been created');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabSubstrateComponent', action: 'Dialog:Insert:Error' });
      })
      .finally(() => {
        this.isReady.emit(true);
        this.dlgVars.visible = false;
        this.computeSubstratesDialogValidation();
      });
  }

  // Removed legacy dlgIsValid wrapper; template should bind directly to isSubstratesDialogValid

  private computeSubstratesDialogValidation() {
    this.isSubstratesDialogValid = !isEmpty(this.catalogId) && !isEmpty(this.substrateId) && (this.leadTime ?? 0) >= 0 && (this.leadTime ?? 0) <= 365;
    return this.isSubstratesDialogValid;
  }

  onSelectRecipe(recipeId: string, substrate: ICatalogSubstrate) {
    const selectedRecipe = this.recipesLookup.find((r) => r.recipeId === recipeId);
    if (!selectedRecipe) {
      notifyAndTrackError(this.msgService, this.appInsights, 'Selected recipe not found', {
        component: 'TabSubstrateComponent',
        action: 'SelectRecipe:NotFound'
      });
      return;
    }
    substrate.recipeJson = selectedRecipe ? `{"recipeId":"${selectedRecipe.recipeId}", "recipeName":"${selectedRecipe.recipeName}"}` : '{}';
    this.updateCatalogSubstrate(substrate);
  }
}
